import { useReducer, useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { CognitoUser as CognitoUserType, CognitoUserSession } from 'amazon-cognito-identity-js';
import { AuthContext } from './AuthContext';
import type { AuthState, AWSCredentials, SignInResult, SignUpParams, AuthUser } from './types';
import {
  createUserPool,
  createCognitoUser,
  authenticateUser,
  signUpUser,
  confirmRegistration,
  resendConfirmationCode,
  forgotPassword as cognitoForgotPassword,
  confirmPassword,
  completeNewPasswordChallenge,
  getSession,
  refreshSession,
  signOut as cognitoSignOut,
  setupTOTP,
  verifyTOTP,
  extractUserFromSession,
  mapCognitoError,
} from './cognito';
import { getIdentityCredentials } from './credentials';
import { config } from '@config/index';

// --- Reducer ---

type AuthAction =
  | { type: 'LOADING' }
  | { type: 'AUTHENTICATED'; user: AuthUser; credentials: AWSCredentials }
  | { type: 'SIGNED_OUT' }
  | { type: 'ERROR'; error: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'CREDENTIALS_UPDATED'; credentials: AWSCredentials };

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  credentials: null,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOADING':
      return { ...state, isLoading: true, error: null };
    case 'AUTHENTICATED':
      return {
        isAuthenticated: true,
        isLoading: false,
        user: action.user,
        credentials: action.credentials,
        error: null,
      };
    case 'SIGNED_OUT':
      return { ...initialState, isLoading: false };
    case 'ERROR':
      return { ...state, isLoading: false, error: action.error };
    case 'CLEAR_ERROR':
      return { ...state, isLoading: false, error: null };
    case 'CREDENTIALS_UPDATED':
      return { ...state, credentials: action.credentials };
    default:
      return state;
  }
}

// --- Helpers ---

/** Credentials buffer: refresh 5 minutes before expiry */
const CREDENTIAL_BUFFER_MS = 5 * 60 * 1000;

function areCredentialsExpired(credentials: AWSCredentials | null): boolean {
  if (!credentials) return true;
  return credentials.expiration.getTime() - Date.now() < CREDENTIAL_BUFFER_MS;
}

async function fetchCredentials(session: CognitoUserSession): Promise<AWSCredentials> {
  const idToken = session.getIdToken().getJwtToken();
  return getIdentityCredentials(
    idToken,
    config.cognitoIdentityPoolId,
    config.cognitoUserPoolId,
    config.region,
  );
}

// --- Provider ---

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const cognitoUserRef = useRef<CognitoUserType | null>(null);
  const credentialsRef = useRef<AWSCredentials | null>(null);

  const userPool = useRef(
    createUserPool(config.cognitoUserPoolId, config.cognitoClientId),
  ).current;

  // --- Session restoration on mount ---
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const currentUser = userPool.getCurrentUser();
        if (!currentUser) {
          dispatch({ type: 'SIGNED_OUT' });
          return;
        }

        const session = await getSession(currentUser);
        if (!session.isValid()) {
          dispatch({ type: 'SIGNED_OUT' });
          return;
        }

        cognitoUserRef.current = currentUser;
        const user = extractUserFromSession(session);
        const credentials = await fetchCredentials(session);
        credentialsRef.current = credentials;
        dispatch({ type: 'AUTHENTICATED', user, credentials });
      } catch {
        dispatch({ type: 'SIGNED_OUT' });
      }
    };

    restoreSession();
  }, [userPool]);

  // --- Auth methods ---

  const signIn = useCallback(async (email: string, password: string): Promise<SignInResult> => {
    dispatch({ type: 'LOADING' });
    try {
      const cognitoUser = createCognitoUser(email, userPool);
      const { result, session, cognitoUser: returnedUser } = await authenticateUser(cognitoUser, email, password);
      cognitoUserRef.current = returnedUser;

      if (result.type === 'SUCCESS' && session) {
        const user = extractUserFromSession(session);
        const credentials = await fetchCredentials(session);
        credentialsRef.current = credentials;
        dispatch({ type: 'AUTHENTICATED', user, credentials });
      } else {
        // MFA_REQUIRED, NEW_PASSWORD_REQUIRED, MFA_SETUP — keep loading false, no error
        dispatch({ type: 'CLEAR_ERROR' });
      }

      return result;
    } catch (err) {
      // If user hasn't verified their email, return a result instead of throwing
      const errObj = err as { code?: string; name?: string };
      if (errObj.code === 'UserNotConfirmedException' || errObj.name === 'UserNotConfirmedException') {
        dispatch({ type: 'CLEAR_ERROR' });
        return { type: 'USER_NOT_CONFIRMED' as const };
      }
      const message = mapCognitoError(err);
      dispatch({ type: 'ERROR', error: message });
      throw new Error(message);
    }
  }, [userPool]);

  const signUp = useCallback(async (params: SignUpParams): Promise<void> => {
    dispatch({ type: 'LOADING' });
    try {
      await signUpUser(userPool, params);
      dispatch({ type: 'CLEAR_ERROR' });
    } catch (err) {
      const message = mapCognitoError(err);
      dispatch({ type: 'ERROR', error: message });
      throw new Error(message);
    }
  }, [userPool]);

  const confirmSignUp = useCallback(async (email: string, code: string): Promise<void> => {
    dispatch({ type: 'LOADING' });
    try {
      const cognitoUser = createCognitoUser(email, userPool);
      await confirmRegistration(cognitoUser, code);
      dispatch({ type: 'CLEAR_ERROR' });
    } catch (err) {
      const message = mapCognitoError(err);
      dispatch({ type: 'ERROR', error: message });
      throw new Error(message);
    }
  }, [userPool]);

  const resendCode = useCallback(async (email: string): Promise<void> => {
    dispatch({ type: 'LOADING' });
    try {
      const cognitoUser = createCognitoUser(email, userPool);
      await resendConfirmationCode(cognitoUser);
      dispatch({ type: 'CLEAR_ERROR' });
    } catch (err) {
      const message = mapCognitoError(err);
      dispatch({ type: 'ERROR', error: message });
      throw new Error(message);
    }
  }, [userPool]);

  const signOutFn = useCallback(async (): Promise<void> => {
    const currentUser = cognitoUserRef.current;
    if (currentUser) {
      cognitoSignOut(currentUser);
    }
    cognitoUserRef.current = null;
    credentialsRef.current = null;
    dispatch({ type: 'SIGNED_OUT' });

    // Clear RTK Query cache to prevent stale data leaking across sessions
    const { store } = await import('@store/index');
    const { baseApi } = await import('@api/baseApi');
    store.dispatch(baseApi.util.resetApiState());
  }, []);

  const forgotPasswordFn = useCallback(async (email: string): Promise<void> => {
    dispatch({ type: 'LOADING' });
    try {
      const cognitoUser = createCognitoUser(email, userPool);
      await cognitoForgotPassword(cognitoUser);
      dispatch({ type: 'CLEAR_ERROR' });
    } catch (err) {
      const message = mapCognitoError(err);
      dispatch({ type: 'ERROR', error: message });
      throw new Error(message);
    }
  }, [userPool]);

  const confirmForgotPassword = useCallback(async (
    email: string,
    code: string,
    newPassword: string,
  ): Promise<void> => {
    dispatch({ type: 'LOADING' });
    try {
      const cognitoUser = createCognitoUser(email, userPool);
      await confirmPassword(cognitoUser, code, newPassword);
      dispatch({ type: 'CLEAR_ERROR' });
    } catch (err) {
      const message = mapCognitoError(err);
      dispatch({ type: 'ERROR', error: message });
      throw new Error(message);
    }
  }, [userPool]);

  const completeNewPassword = useCallback(async (newPassword: string): Promise<void> => {
    dispatch({ type: 'LOADING' });
    try {
      const currentUser = cognitoUserRef.current;
      if (!currentUser) throw new Error('No active authentication session.');

      const session = await completeNewPasswordChallenge(currentUser, newPassword);
      const user = extractUserFromSession(session);
      const credentials = await fetchCredentials(session);
      credentialsRef.current = credentials;
      dispatch({ type: 'AUTHENTICATED', user, credentials });
    } catch (err) {
      const message = mapCognitoError(err);
      dispatch({ type: 'ERROR', error: message });
      throw new Error(message);
    }
  }, []);

  const setupMFA = useCallback(async (): Promise<string> => {
    try {
      const currentUser = cognitoUserRef.current;
      if (!currentUser) throw new Error('No active authentication session.');
      return await setupTOTP(currentUser);
    } catch (err) {
      const message = mapCognitoError(err);
      dispatch({ type: 'ERROR', error: message });
      throw new Error(message);
    }
  }, []);

  const verifyMFAFn = useCallback(async (code: string): Promise<void> => {
    try {
      const currentUser = cognitoUserRef.current;
      if (!currentUser) throw new Error('No active authentication session.');
      await verifyTOTP(currentUser, code);
    } catch (err) {
      const message = mapCognitoError(err);
      dispatch({ type: 'ERROR', error: message });
      throw new Error(message);
    }
  }, []);

  const getCredentialsFn = useCallback(async (): Promise<AWSCredentials> => {
    // Return cached credentials if still valid
    if (credentialsRef.current && !areCredentialsExpired(credentialsRef.current)) {
      return credentialsRef.current;
    }

    // Refresh credentials via a new session
    try {
      const currentUser = cognitoUserRef.current ?? userPool.getCurrentUser();
      if (!currentUser) {
        dispatch({ type: 'SIGNED_OUT' });
        throw new Error('No authenticated session. Please sign in again.');
      }

      const session = await getSession(currentUser);
      if (!session.isValid()) {
        dispatch({ type: 'SIGNED_OUT' });
        throw new Error('Session expired. Please sign in again.');
      }

      const credentials = await fetchCredentials(session);
      credentialsRef.current = credentials;
      dispatch({ type: 'CREDENTIALS_UPDATED', credentials });

      // Also refresh user info in case groups changed
      const user = extractUserFromSession(session);
      dispatch({ type: 'AUTHENTICATED', user, credentials });

      return credentials;
    } catch (err) {
      // Token refresh failed — clear session
      cognitoUserRef.current = null;
      credentialsRef.current = null;
      dispatch({ type: 'SIGNED_OUT' });
      throw err;
    }
  }, [userPool]);

  const forceRefreshSession = useCallback(async () => {
    const currentUser = cognitoUserRef.current;
    if (!currentUser) throw new Error('No authenticated user');
    const session = await refreshSession(currentUser);
    const user = extractUserFromSession(session);
    const credentials = await fetchCredentials(session);
    credentialsRef.current = credentials;
    dispatch({ type: 'AUTHENTICATED', user, credentials });
  }, []);

  const contextValue = {
    ...state,
    signIn,
    signUp,
    confirmSignUp,
    resendCode,
    signOut: signOutFn,
    forgotPassword: forgotPasswordFn,
    confirmForgotPassword,
    completeNewPassword,
    setupMFA,
    verifyMFA: verifyMFAFn,
    getCredentials: getCredentialsFn,
    forceRefreshSession,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
